import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Heading,
  Text,
  Skeleton,
  Icon,
  Flex,
  useColorModeValue,
} from "@chakra-ui/react";

import { RunnerResponse } from "../types";
import { formatDate, formatSnack2Readable } from "../utils/helpers";
import { FaCheck, FaTimes } from "react-icons/fa";
import { useVirtualizer } from "@tanstack/react-virtual";

interface DataEntryTableProps {
  data: RunnerResponse[];
  isLoading: boolean;
  error: unknown;
}

// Add function to check if record is active
const isActive = (lastUpdated: string): boolean => {
  const lastUpdatedTime = new Date(lastUpdated).getTime();
  const currentTime = new Date().getTime();
  const thresholdMs = 60 * 1000; // Convert seconds to milliseconds and double it

  return currentTime - lastUpdatedTime <= thresholdMs;
};

const RunnerTable: React.FC<DataEntryTableProps> = ({
  data,
  isLoading,
  error,
}) => {
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const tableBg = useColorModeValue("white", "gray.800");
  const headerBg = useColorModeValue("gray.50", "gray.700");

  // Get all unique keys from all entries to create table headers
  const allKeys = useMemo(() => {
    if (!data.length) return [];

    const keySet = new Set<string>();
    data.forEach((item) => {
      if (item) {
        Object.keys(item).forEach((key) => {
          if (key === "user_id" || key === "updated_at") {
            return;
          }
          keySet.add(key);
        });
      }
    });

    return ["Active", ...Array.from(keySet)];
  }, [data]);

  const renderItem = (key: string, item: RunnerResponse) => {
    // @ts-expect-error exist\
    const value = item[key];
    if (item && value !== undefined) {
      if (typeof value === "object") {
        return JSON.stringify(value);
      }
      if (key === "created_at" || key === "last_seen") {
        return formatDate(value);
      }
      return value;
    }
    return "-";
  };

  const pageSize = 50;
  const [visibleCount, setVisibleCount] = useState(pageSize);
  useEffect(() => {
    setVisibleCount(pageSize);
  }, [data.length]);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const hasMore = visibleCount < data.length;
  const virtualCount = isLoading
    ? 12
    : Math.min(visibleCount, data.length) + (hasMore ? 1 : 0);

  const rowVirtualizer = useVirtualizer({
    count: virtualCount,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 44,
    overscan: 10,
  });

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (!hasMore) return;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining < 300) {
      setVisibleCount((prev) => Math.min(prev + pageSize, data.length));
    }
  };

  if (error) {
    return (
      <Box textAlign="center" py={10} px={6}>
        <Heading as="h2" size="lg" color="red.500">
          Error Loading Data
        </Heading>
        <Text mt={4} color="gray.500">
          {error instanceof Error
            ? error.message
            : "An unexpected error occurred"}
        </Text>
      </Box>
    );
  }

  // Column sizing similar to the previous <Table> behavior:
  // keep a couple of small fixed columns, let the rest grow/shrink with the container.
  const colFlex = (key: string) => {
    if (key === "Active") return "0 0 90px";
    if (key === "id") return "0 0 110px";
    if (key === "ip_address") return "0 0 120px";
    if (key === "runner_id") return "0 0 400px";
    return "1 1 220px";
  };

  const colMinW = (key: string) => {
    if (key === "Active") return "90px";
    if (key === "id") return "110px";
    if (key === "ip_address") return "120px";
    if (key === "runner_id") return "400px";
    return "160px";
  };

  return (
    <Box
    borderRadius="md"
    boxShadow="sm"
    bg={tableBg}
    border="1px"
    borderColor={borderColor}
    overflow="hidden"
    minW="1300px"
  >
    {/* Header */}
    <Flex
      px={4}
      py={3}
      borderBottom="1px"
      borderColor={borderColor}
      bg={headerBg}
      fontSize="sm"
      fontWeight="semibold"
      overflowX="auto"
    >
      {allKeys.map((key) => (
        <Box key={key} flex={colFlex(key)} minW={colMinW(key)} pr={3}>
          {formatSnack2Readable(key)}
        </Box>
      ))}
    </Flex>

    {!isLoading && data.length === 0 ? (
      <Box p={8} textAlign="center">
        <Text color="gray.500">No data entries found</Text>
      </Box>
    ) : (
      <Box
        ref={scrollRef}
        maxH="50vh"
        overflow="auto"
        onScroll={handleScroll}
      >
        <Box
          height={`${rowVirtualizer.getTotalSize()}px`}
          position="relative"
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const idx = virtualRow.index;
            const isLoaderRow =
              !isLoading &&
              hasMore &&
              idx === Math.min(visibleCount, data.length);
            const item = !isLoading ? data[idx] : null;

            return (
              <Flex
                key={virtualRow.key}
                position="absolute"
                top={0}
                left={0}
                right={0}
                transform={`translateY(${virtualRow.start}px)`}
                height={`${virtualRow.size}px`}
                px={4}
                align="center"
                borderBottom="1px"
                borderColor={borderColor}
                fontSize="sm"
                overflowX="auto"
              >
                {isLoading ? (
                  allKeys.map((key) => (
                    <Box
                      key={key}
                      flex={colFlex(key)}
                      minW={colMinW(key)}
                      pr={3}
                    >
                      <Skeleton height="16px" />
                    </Box>
                  ))
                ) : isLoaderRow ? (
                  <Box width="100%" textAlign="center" color="gray.500">
                    Showing {Math.min(visibleCount, data.length)} /{" "}
                    {data.length}. Scroll to load more...
                  </Box>
                ) : item ? (
                  allKeys.map((key) => {
                    if (key === "Active") {
                      return (
                        <Box
                          key={key}
                          flex={colFlex(key)}
                          minW={colMinW(key)}
                          pr={3}
                        >
                          {isActive(item.last_seen) ? (
                            <Icon
                              as={FaCheck}
                              color="green.500"
                              boxSize={4}
                            />
                          ) : (
                            <Icon
                              as={FaTimes}
                              color="red.500"
                              boxSize={4}
                            />
                          )}
                        </Box>
                      );
                    }
                    return (
                      <Box
                        key={key}
                        flex={colFlex(key)}
                        minW={colMinW(key)}
                        pr={3}
                        noOfLines={1}
                      >
                        {renderItem(key, item)}
                      </Box>
                    );
                  })
                ) : null}
              </Flex>
            );
          })}
        </Box>
      </Box>
    )}
  </Box>
  );
};

export default RunnerTable;
